import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
} from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { sendSMSNotificationForCancellation, sendSMSNotificationForNewMessage } from '../../ducks/smsNotifications.duck';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const FETCH_SOURCE_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_SOURCE_MESSAGES_REQUEST';
export const FETCH_SOURCE_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_SOURCE_MESSAGES_SUCCESS';
export const FETCH_SOURCE_MESSAGES_ERROR = 'app/TransactionPage/FETCH_SOURCE_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const UPLOAD_MESSAGE_IMAGE_REQUEST = 'app/TransactionPage/UPLOAD_MESSAGE_IMAGE_REQUEST';
export const UPLOAD_MESSAGE_IMAGE_SUCCESS = 'app/TransactionPage/UPLOAD_MESSAGE_IMAGE_SUCCESS';
export const UPLOAD_MESSAGE_IMAGE_ERROR = 'app/TransactionPage/UPLOAD_MESSAGE_IMAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const UPDATE_PRICE_ADJUSTMENTS_REQUEST = 'app/TransactionPage/UPDATE_PRICE_ADJUSTMENTS_REQUEST';
export const UPDATE_PRICE_ADJUSTMENTS_SUCCESS = 'app/TransactionPage/UPDATE_PRICE_ADJUSTMENTS_SUCCESS';
export const UPDATE_PRICE_ADJUSTMENTS_ERROR = 'app/TransactionPage/UPDATE_PRICE_ADJUSTMENTS_ERROR';

export const ACCEPT_WITH_ADJUSTMENTS_REQUEST = 'app/TransactionPage/ACCEPT_WITH_ADJUSTMENTS_REQUEST';
export const ACCEPT_WITH_ADJUSTMENTS_SUCCESS = 'app/TransactionPage/ACCEPT_WITH_ADJUSTMENTS_SUCCESS';
export const ACCEPT_WITH_ADJUSTMENTS_ERROR = 'app/TransactionPage/ACCEPT_WITH_ADJUSTMENTS_ERROR';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_TIME_SLOTS_FOR_DATE_REQUEST =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_REQUEST';
export const FETCH_TIME_SLOTS_FOR_DATE_SUCCESS =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_SUCCESS';
export const FETCH_TIME_SLOTS_FOR_DATE_ERROR =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

export const SHOW_CONFLICT_WARNING = 'app/TransactionPage/SHOW_CONFLICT_WARNING';
export const HIDE_CONFLICT_WARNING = 'app/TransactionPage/HIDE_CONFLICT_WARNING';
export const SET_CONFLICTING_LEADS = 'app/TransactionPage/SET_CONFLICTING_LEADS';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  fetchSourceMessagesInProgress: false,
  fetchSourceMessagesError: null,
  sourceMessages: [],
  totalSourceMessages: 0,
  totalSourceMessagePages: 0,
  oldestSourceMessagePageFetched: 0,
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  uploadMessageImageInProgress: false,
  uploadMessageImageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  conflictWarningOpen: false,
  conflictingLeads: [],
  updatePriceAdjustmentsInProgress: false,
  updatePriceAdjustmentsError: null,
  acceptWithAdjustmentsInProgress: false,
  acceptWithAdjustmentsError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
      };
    case TRANSITION_SUCCESS:
      return { ...state, transitionInProgress: null };
    case TRANSITION_ERROR:
      return {
        ...state,
        transitionInProgress: null,
        transitionError: payload,
      };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case FETCH_SOURCE_MESSAGES_REQUEST:
      return { ...state, fetchSourceMessagesInProgress: true, fetchSourceMessagesError: null };
    case FETCH_SOURCE_MESSAGES_SUCCESS: {
      const oldestSourceMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchSourceMessagesInProgress: false,
        sourceMessages: mergeEntityArrays(state.sourceMessages, payload.messages),
        totalSourceMessages: payload.totalItems,
        totalSourceMessagePages: payload.totalPages,
        oldestSourceMessagePageFetched,
      };
    }
    case FETCH_SOURCE_MESSAGES_ERROR:
      return { ...state, fetchSourceMessagesInProgress: false, fetchSourceMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case UPLOAD_MESSAGE_IMAGE_REQUEST:
      return {
        ...state,
        uploadMessageImageInProgress: true,
        uploadMessageImageError: null,
      };
    case UPLOAD_MESSAGE_IMAGE_SUCCESS:
      return { ...state, uploadMessageImageInProgress: false };
    case UPLOAD_MESSAGE_IMAGE_ERROR:
      return { ...state, uploadMessageImageInProgress: false, uploadMessageImageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_REQUEST: {
      const timeSlotsForDate = {
        ...removeOutdatedDateData(state.timeSlotsForDate),
        [payload]: {
          ...state.timeSlotsForDate[payload],
          fetchTimeSlotsError: null,
          fetchedAt: null,
          fetchTimeSlotsInProgress: true,
          timeSlots: [],
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_SUCCESS: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchedAt: new Date().getTime(),
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_ERROR: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, timeSlotsForDate };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SHOW_CONFLICT_WARNING:
      return { ...state, conflictWarningOpen: true };
    case HIDE_CONFLICT_WARNING:
      return { ...state, conflictWarningOpen: false };
    case SET_CONFLICTING_LEADS:
      return { ...state, conflictingLeads: payload };

    case UPDATE_PRICE_ADJUSTMENTS_REQUEST:
      return {
        ...state,
        updatePriceAdjustmentsInProgress: true,
        updatePriceAdjustmentsError: null,
      };
    case UPDATE_PRICE_ADJUSTMENTS_SUCCESS:
      return {
        ...state,
        updatePriceAdjustmentsInProgress: false,
      };
    case UPDATE_PRICE_ADJUSTMENTS_ERROR:
      return {
        ...state,
        updatePriceAdjustmentsInProgress: false,
        updatePriceAdjustmentsError: payload,
      };

    case ACCEPT_WITH_ADJUSTMENTS_REQUEST:
      return {
        ...state,
        acceptWithAdjustmentsInProgress: true,
        acceptWithAdjustmentsError: null,
      };
    case ACCEPT_WITH_ADJUSTMENTS_SUCCESS:
      return {
        ...state,
        acceptWithAdjustmentsInProgress: false,
      };
    case ACCEPT_WITH_ADJUSTMENTS_ERROR:
      return {
        ...state,
        acceptWithAdjustmentsInProgress: false,
        acceptWithAdjustmentsError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = state => {
  return state.TransactionPage.transitionInProgress;
};

export const conflictWarningOpen = state => {
  return state.TransactionPage.conflictWarningOpen;
};

export const conflictingLeads = state => {
  return state.TransactionPage.conflictingLeads;
};

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = e => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const transitionRequest = transitionName => ({ type: TRANSITION_REQUEST, payload: transitionName });
const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = e => ({ type: TRANSITION_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const fetchSourceMessagesRequest = () => ({ type: FETCH_SOURCE_MESSAGES_REQUEST });
const fetchSourceMessagesSuccess = (messages, pagination) => ({
  type: FETCH_SOURCE_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchSourceMessagesError = e => ({ type: FETCH_SOURCE_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const uploadMessageImageRequest = () => ({ type: UPLOAD_MESSAGE_IMAGE_REQUEST });
const uploadMessageImageSuccess = (imageData) => ({ type: UPLOAD_MESSAGE_IMAGE_SUCCESS, payload: imageData });
const uploadMessageImageError = e => ({ type: UPLOAD_MESSAGE_IMAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

export const fetchMonthlyTimeSlotsRequest = monthId => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchTimeSlotsForDateRequest = dateId => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_REQUEST,
  payload: dateId,
});
export const fetchTimeSlotsForDateSuccess = (dateId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_SUCCESS,
  payload: { timeSlots, dateId },
});
export const fetchTimeSlotsForDateError = (dateId, error) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_ERROR,
  error: true,
  payload: { dateId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const showConflictWarning = () => ({ type: SHOW_CONFLICT_WARNING });
export const hideConflictWarning = () => ({ type: HIDE_CONFLICT_WARNING });

export const hideConflictWarningAndResetTransition = () => (dispatch) => {
  dispatch(hideConflictWarning());
  dispatch(transitionSuccess());
};
export const setConflictingLeads = leads => ({ type: SET_CONFLICTING_LEADS, payload: leads });

export const confirmLeadAcceptanceWithConflicts = (txId, params) => (dispatch, getState, sdk) => {
  // Hide the conflict warning modal
  dispatch(hideConflictWarning());
  
  // Set transition in progress for the accept action
  dispatch(transitionRequest('transition/accept'));
  
  // Proceed with the actual acceptance
  return handleLeadAcceptance(txId, params, dispatch, sdk, true);
};

export const updatePriceAdjustmentsRequest = () => ({
  type: UPDATE_PRICE_ADJUSTMENTS_REQUEST,
});

export const updatePriceAdjustmentsSuccess = () => ({
  type: UPDATE_PRICE_ADJUSTMENTS_SUCCESS,
});

export const updatePriceAdjustmentsError = error => ({
  type: UPDATE_PRICE_ADJUSTMENTS_ERROR,
  payload: error,
  error: true,
});

export const acceptWithAdjustmentsRequest = () => ({
  type: ACCEPT_WITH_ADJUSTMENTS_REQUEST,
});

export const acceptWithAdjustmentsSuccess = () => ({
  type: ACCEPT_WITH_ADJUSTMENTS_SUCCESS,
});

export const acceptWithAdjustmentsError = error => ({
  type: ACCEPT_WITH_ADJUSTMENTS_ERROR,
  payload: error,
  error: true,
});


// ================ Thunks ================ //

/**
 * Update price adjustments on a transaction
 * This uses the update-price-adjustments transition which can be called multiple times
 * 
 * @param {UUID} transactionId - The transaction ID (UUID object or string)
 * @param {Array} adjustments - Array of adjustment objects with {amount, label, addedBy, addedAt}
 */
export const updatePriceAdjustments = (transaction, adjustments) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updatePriceAdjustmentsRequest());

  const txId = transaction?.id;

  const listing = transaction?.listing;
  const protectedData = transaction?.attributes?.protectedData || {};

  const petsArray = Array.isArray(protectedData.pets)
    ? protectedData.pets
    : Array.isArray(protectedData.pets?.items)
      ? protectedData.pets.items
      : [];

  // Build orderData with adjustments
  const orderData = {
    bookingStart: protectedData.leadStart,
    bookingEnd: protectedData.leadEnd,
    pets: petsArray,
    gratuity: protectedData.gratuity,
    priceAdjustments: adjustments,  // ← Include adjustments!
    unitType: listing?.attributes?.publicData?.unitType,
  };

  console.log('Fetching line items with adjustments:', orderData);
  
  // Step 1: Call backend to calculate line items
  return transactionLineItems({
    orderData,
    listingId: listing.id.uuid,
    isOwnListing: true
  })
    .then(response => {
      const calculatedLineItems = response.data;
      
      console.log('Backend returned line items:', calculatedLineItems);

      // Step 2: Pass calculated line items to transition
      return sdk.transactions.transition(
        {
          id: txId,
          transition: 'transition/update-price-adjustments',
          params: {
            protectedData: {
              priceAdjustments: adjustments,
              lastUpdatedAt: new Date().toISOString(),
            },
            lineItems: calculatedLineItems  // ← PASS THEM HERE!
          },
        },
        { expand: true }
      );
    })
    .then(response => {
      console.log('Transition success!');
      dispatch(addMarketplaceEntities(response));
      dispatch(updatePriceAdjustmentsSuccess());
      return response;
    })
    .catch(e => {
      console.error('Error:', e);
      dispatch(updatePriceAdjustmentsError(storableError(e)));
      throw e;
    });
};

/**
 * Accept lead request with finalized price adjustments
 * This is a two-step process:
 * 1. Save final adjustments
 * 2. Transition to accepted state
 * 
 * @param {string} transactionId - Transaction UUID
 * @param {Array} adjustments - Final price adjustment objects
 * @param {Object} config - App config
 */
export const acceptWithAdjustments = (transactionId, adjustments, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(acceptWithAdjustmentsRequest());

  const txId = new UUID(transactionId);

  // Step 1: Update adjustments one final time
  const updateParams = {
    protectedData: {
      priceAdjustments: adjustments,
      finalPriceAdjustments: adjustments, // Lock the final version
      adjustmentsFinalized: true,
      finalizedAt: new Date().toISOString(),
      lastUpdatedBy: 'provider',
    },
  };

  return sdk.transactions
    .transition(
      { 
        id: txId, 
        transition: 'transition/update-price-adjustments', 
        params: updateParams 
      },
      { 
        expand: true,
        ...getImageVariants(config.layout.listingImage),
      }
    )
    .then(updateResponse => {
      dispatch(addMarketplaceEntities(updateResponse));
      
      // Step 2: Accept the lead request
      const acceptParams = {
        protectedData: {
          // Copy over the finalized adjustments
          ...updateResponse.data.data.attributes.protectedData,
        },
      };

      return sdk.transactions.transition(
        { 
          id: txId, 
          transition: 'transition/accept', 
          params: acceptParams 
        },
        { 
          expand: true,
          ...getImageVariants(config.layout.listingImage),
        }
      );
    })
    .then(acceptResponse => {
      dispatch(addMarketplaceEntities(acceptResponse));
      dispatch(acceptWithAdjustmentsSuccess());
      
      // Refresh transaction data and transitions
      return Promise.all([
        dispatch(fetchTransaction(txId, 'provider', config)),
        dispatch(fetchNextTransitions(txId)),
      ]);
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'accept-with-adjustments-failed', {
        transactionId: transactionId,
        adjustments,
      });
      dispatch(acceptWithAdjustmentsError(error));
      throw error;
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    dispatch(fetchTimeSlotsForDateRequest(dateId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        dispatch(fetchTimeSlotsForDateSuccess(dateId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        dispatch(fetchTimeSlotsForDateError(dateId, storableError(e)));
        return [];
      });
  } else {
    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyTimeSlotsRequest(monthId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        dispatch(fetchMonthlyTimeSlotsSuccess(monthId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
        return [];
      });
  }
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

/**
 * Injects a transaction's included provider relationship as the included
 * listing's author relationship.
 * @param {*} txResponse A SDK response from transactions.show().
 * @returns a copy of the txResponse parameter with a listing.author
 * relationship added, if provider exists in the transaction's relationships.
 */
const injectAuthorRelationship = txResponse => {
  const {
    included,
    data: {
      relationships: { provider },
    },
  } = txResponse.data;

  // If provider has not been included, return the response with no changes.
  if (!provider?.data?.id) {
    return txResponse;
  }

  const includedListingIdx = included.findIndex(inc => inc.type === 'listing');

  // We will set the transaction's provider as the listing's author.
  // The full user resource we want to associate with the listing is
  // already available in the response.data.included array, so we only
  // need to add a relationship reference in the included listing resource.
  included[includedListingIdx] = {
    ...included[includedListingIdx],
    relationships: {
      ...included[includedListingIdx].relationships,
      author: {
        data: {
          id: provider?.data?.id,
          type: 'user',
        },
      },
    },
  };

  return {
    ...txResponse,
    data: {
      ...txResponse.data,
      included: [...included],
    },
  };
};

export const fetchTransaction = (id, txRole, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'listing.images',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then((response) => {
      const {
        leadTransactionId,
      } = response?.data?.data?.attributes?.protectedData || {};
      if (leadTransactionId && leadTransactionId !== id) {
        dispatch(fetchSourceMessages(new UUID(leadTransactionId), 1, config))
      }
      return Promise.resolve(response);
    })
    .then(response => {
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];
      const processName = resolveLatestProcessName(transaction.attributes.processName);

      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && isInquiry;

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      // API does not allow fetching transaction.listing.author, so we will
      // set the relationship manually based on the transaction's provider.
      return injectAuthorRelationship(response);
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchTransactionSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

// Function to check for overlapping leads without cancelling them
const checkOverlappingLeads = async (transaction, sdk) => {
  try {
    const leadStart = transaction?.attributes?.protectedData?.leadStart;
    const leadEnd = transaction?.attributes?.protectedData?.leadEnd;
    const listingId = transaction?.relationships?.listing?.data?.id;

    if (!leadStart || !leadEnd || !listingId) {
      return [];
    }

    // Query for pending leads on the same listing
    const pendingLeadsResponse = await sdk.transactions.query({
      only: 'sale',
      lastTransitions: ['transition/request'],
      processNames: ['request-lead'],
      listingId: listingId,
      perPage: 100,
      include: ['listing, customer']
    });

    const overlappingLeads = pendingLeadsResponse.data.data.filter(tx => {
      // Skip the current transaction
      if (tx.id.uuid === transaction.id.uuid) return false;
      
      const txProtectedData = tx.attributes.protectedData;
      if (!txProtectedData.leadStart || !txProtectedData.leadEnd) return false;
      
      // Check for time overlap
      const start1 = new Date(leadStart);
      const end1 = new Date(leadEnd);
      const start2 = new Date(txProtectedData.leadStart);
      const end2 = new Date(txProtectedData.leadEnd);
      
      return start1 < end2 && end1 > start2;
    });

    // Fetch user data for each conflicting lead to get customer names
    const enrichedLeads = await Promise.all(
      overlappingLeads.map(async (lead) => {
        try {
          const customerId = lead.relationships.customer.data.id;
          const userResponse = await sdk.users.show({
            id: customerId,
            include: ['profile']
          });
          
          // Merge user data with lead data
          return {
            ...lead,
            relationships: {
              ...lead.relationships,
              customer: {
                data: {
                  ...lead.relationships.customer.data,
                  attributes: {
                    ...lead.relationships.customer.data.attributes,
                    profile: userResponse.data.data.attributes.profile
                  }
                }
              }
            }
          };
        } catch (userError) {
          console.error('Error fetching user data for customer:', customerId, userError);
          // Return lead without enriched user data if fetch fails
          return lead;
        }
      })
    );

    return enrichedLeads;
  } catch (error) {
    console.error('Error checking overlapping leads:', error);
    return [];
  }
};

// Special handler for lead acceptance with overlap cancellation
const handleLeadAcceptance = (txId, params, dispatch, sdk, doCancelOverlappingLeads = false) => {
  return sdk.transactions
    .transition({ id: txId, transition: 'transition/accept', params }, { expand: true, include: ['listing'] })
    .then(async (response) => {
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());

      // // After successful acceptance, check for overlapping leads to cancel
      if (doCancelOverlappingLeads) {
        await cancelOverlappingLeads(response.data.data, sdk, dispatch);
      }

      refreshTransactionEntity(sdk, txId, dispatch);
      return response;
    })
    .catch(e => {
      dispatch(transitionError(storableError(e)));
      console.log('Lead acceptance failed', e.message);
      log.error(e, 'accept-lead-failed', {
        txId,
        transition: 'transition/accept',
      });
      throw e;
    });
};

// New handler for lead acceptance with conflict warning
const handleLeadAcceptanceWithConflictCheck = (txId, params, dispatch, sdk) => {
  return sdk.transactions
    .query({
      processNames: ['request-lead'],
      lastTransitions: ['transition/request', 'transition/update-price-adjustments'],
      perPage: 100,
      include: ['listing']
    })
    .then(async (response) => {
      const transactions = response.data.data;
      const transaction = transactions.find(tx => tx.id.uuid === txId.uuid);

      if (!transaction) {
        console.error('Transaction not found', txId);
        return;
      }

      let overlappingLeads = [];
      const protectedData = transaction.attributes.protectedData;
      if (protectedData.leadStart && protectedData.leadEnd) {
        overlappingLeads = await checkOverlappingLeads(transaction, sdk);
      }
      
      if (overlappingLeads.length > 0) {
        // Store conflicting leads and show warning modal
        dispatch(setConflictingLeads(overlappingLeads));
        dispatch(showConflictWarning());
        // Reset transition state since we're showing modal instead of proceeding
        dispatch(transitionSuccess());
        return { hasConflicts: true, conflictingLeads: overlappingLeads };
      } else {
        // No conflicts, proceed with acceptance
        return handleLeadAcceptance(txId, params, dispatch, sdk);
      }
    })
    .catch(e => {
      console.log('Error checking conflicts:', e.message);
      // If conflict check fails, proceed with normal acceptance
      return handleLeadAcceptance(txId, params, dispatch, sdk);
    });
};

// Function to find and cancel overlapping leads
const cancelOverlappingLeads = async (acceptedTransaction, sdk, dispatch) => {
  try {
    const leadStart = acceptedTransaction?.attributes?.protectedData?.leadStart;
    const leadEnd = acceptedTransaction?.attributes?.protectedData?.leadEnd;
    const listingId = acceptedTransaction?.relationships?.listing?.data?.id;

    if (!leadStart || !leadEnd || !listingId) {
      console.warn("Missing leadStart/leadEnd/listingId. Skipping cancelOverlappingLeads.", {
        leadStart,
        leadEnd,
        listingId,
      });
      return;
    }

    // Query for pending leads on the same listing
    const pendingLeadsResponse = await sdk.transactions.query({
      only: 'sale',
      lastTransitions: ['transition/request'],
      processNames: ['request-lead'],
      listingId: listingId,
      perPage: 100
    });

    const overlappingLeads = pendingLeadsResponse.data.data.filter(tx => {
      // Skip the current transaction
      if (tx.id.uuid === acceptedTransaction.id.uuid) return false;
      
      const txProtectedData = tx.attributes.protectedData;
      if (!txProtectedData.leadStart || !txProtectedData.leadEnd) return false;
      
      // Check for time overlap
      const start1 = new Date(leadStart);
      const end1 = new Date(leadEnd);
      const start2 = new Date(txProtectedData.leadStart);
      const end2 = new Date(txProtectedData.leadEnd);
      
      return start1 < end2 && end1 > start2;
    });

    // Cancel overlapping leads
    const cancellationPromises = overlappingLeads.map(async (leadTx) => {
      try {
        const cancelResponse = await sdk.transactions.transition({
          id: leadTx.id,
          transition: 'transition/decline',
          params: {
            protectedData: {
              cancellationReason: 'overlapping_booking_accepted',
              acceptedLeadId: acceptedTransaction.id.uuid,
              cancelledAt: new Date().toISOString()
            }
          }
        }, { expand: true });

        // Update the store with the cancelled transaction
        dispatch(addMarketplaceEntities(cancelResponse));
        
        return cancelResponse;
      } catch (error) {
        console.error(`Failed to cancel overlapping lead ${leadTx.id.uuid}:`, error);
        return null;
      }
    });

    await Promise.allSettled(cancellationPromises);
    
    // Refresh notifications to show cancelled leads
    dispatch(fetchCurrentUserNotifications());
    
  } catch (error) {
    console.error('Error cancelling overlapping leads:', error);
    // Don't throw - the main acceptance was successful
  }
};

export const makeTransition = (txId, transitionName, processName, params) => (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(transitionName));
  // console.log('makeTransition', { txId, transitionName, processName, params});
  // For lead acceptance, use enhanced backend endpoint with conflict checking
  if (transitionName === 'transition/accept' && processName === 'request-lead') {
    return handleLeadAcceptanceWithConflictCheck(txId, params, dispatch, sdk);
  }

  return sdk.transactions
    .transition({ id: txId, transition: transitionName, params }, { expand: true })
    .then(async (response) => {
      const { status, data } = response;
      const leadTransactionId = data?.data?.attributes?.protectedData?.leadTransactionId;
      const processName = data?.data?.attributes?.processName;
      const lastTransition = data?.data?.attributes?.lastTransition;
      // console.log('initiate data', status, leadTransactionId, data?.data.attributes);
      // console.log(data?.data?.attributes?.processName, data?.data?.attributes?.lastTransition);
      if (
        status === 200
        && leadTransactionId
        && processName === 'default-booking'
        && lastTransition === 'transition/accept'
      ) {
        await sdk.transactions.transition({
          id: new UUID(leadTransactionId),
          transition: "transition/confirm-booking",
          params: {
            protectedData: {
              isDone: true,
            },
          },
        })
        .catch((e) => {
          console.log('Transaction transition failed', e.message);
        });
        // console.log('Transaction transition successful', transition_results);
      }
      return Promise.resolve(response);
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());

      if (transitionName === 'transition/cancel-by-customer') {
        // Send SMS notification to provider about the cancellation
        dispatch(sendSMSNotificationForCancellation(txId, { 
          marketplaceName: 'marketplace',
          notificationType: 'cancellation'
        }));
      }
      
      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      dispatch(transitionError(storableError(e)));
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      throw e;
    });
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

const fetchSourceMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchSourceMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchSourceMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch(e => {
      dispatch(fetchSourceMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const sendMessage = (txId, message, config, imageData = null) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  // Prepare message content with image if provided
  let messageContent;
  
  if (imageData && imageData.id) {
    const imageId = imageData.id.uuid;
    const defaultUrl = imageData.attributes?.variants?.default?.url;
    const imageUrl = defaultUrl || `https://sharetribe.imgix.net/${imageId}`;
    
    if (message && message.trim()) {
      // Text + Image: create HTML content
      messageContent = `${message} <br><br><img src='${imageUrl}' alt='Message attachment' style='max-width:100%;height:auto;'/>`;
    } else {
      // Image only: create HTML content
      messageContent = `<img src='${imageUrl}' alt='Message attachment' style='max-width:100%;height:auto;'/>`;
    }
  } else {
    // Text only: store as plain text
    messageContent = message;
  }

  return sdk.messages
    .send({ 
      transactionId: txId, 
      content: messageContent
    })
    .then(response => {
      const messageId = response.data.data.id;


      // Send SMS/MMS notification for new message
      dispatch(sendSMSNotificationForNewMessage(txId, message, imageData, {
        marketplaceName: config.marketplaceName || 'marketplace'
      }));

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => dispatch(sendMessageSuccess()));
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

export const uploadMessageImage = (file, config) => (dispatch, getState, sdk) => {
  dispatch(uploadMessageImageRequest());

  const bodyParams = {
    image: file,
  };
  const queryParams = {
    expand: true,
    'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x'],
  };

  return sdk.images
    .upload(bodyParams, queryParams)
    .then(response => {
      const uploadedImage = response.data.data;
      dispatch(uploadMessageImageSuccess(uploadedImage));
      return uploadedImage;
    })
    .catch(e => {
      dispatch(uploadMessageImageError(storableError(e)));
      throw e;
    });
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(id, params, role, dispatch, sdk);
      } else {
        dispatch(sendReviewError(storableError(e)));

        // Rethrow so the page can track whether the sending failed, and
        // keep the message in the form for a retry.
        throw e;
      }
    });
};

export const sendReview = (tx, transitionOptionsInfo, params, config) => (
  dispatch,
  getState,
  sdk
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  dispatch(sendReviewRequest());

  return hasOtherPartyReviewedFirst
    ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
    : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch(e => {
      dispatch(fetchTransitionsError(storableError(e)));
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  // Sale / order (i.e. transaction entity in API)
  return Promise.all([
    dispatch(fetchTransaction(txId, txRole, config)),
    dispatch(fetchMessages(txId, 1, config)),
    dispatch(fetchNextTransitions(txId)),
  ]);
};
